import type {
  BoxProps,
  IntrinsicSystemProps,
  MotionPropsFor,
} from '@hover/blueprint';
import {
  Box,
  Center,
  Flex,
  Icon,
  Motion,
  calc,
  cssVar,
  forwardRef,
  motion,
  transition,
  useBreakpointValue,
  useTheme,
} from '@hover/blueprint';
import type { Space } from '@hover/blueprint/foundation';
import { iconChevronLeft, iconChevronRight } from '@hover/icons';
import { isTruthy, mapValues } from '@hover/utilities';
import { AnimatePresence } from 'framer-motion';
import React, { useRef } from 'react';

import { useHomeownerTranslate } from 'src/modules/homeownerApp/hooks';
import { useMaxWidthQuery } from 'src/shared/hooks/scrollHooks/useMaxWidthQuery';
import { useScrollFade } from 'src/shared/hooks/scrollHooks/useScrollFade';
import { useScrollTo } from 'src/shared/hooks/scrollHooks/useScrollTo';
import { fadePresence } from 'src/shared/utils/motion';
import { hideScrollbars } from 'src/shared/utils/styles';

import { MaterialCalloutButton } from '../../materialCallouts';
import { CustomizeSelectPaletteButton } from '../CustomizeSelectPaletteButton';
import { SubRegionsTabs, buttonStyles, useSubRegions } from '../SubRegions';
import {
  useCustomization,
  useCustomizationSegmentation,
} from '../useCustomization';
import { useCustomizationMaterialCallout } from '../useCustomizeMaterialCallout';
import { useIsSubregionSelectionEnabled } from '../useIsSubregionSelectionEnabled';

const MotionMaterialCalloutButton = motion(MaterialCalloutButton);
const MotionFlex = motion(Flex);
const MotionBoxHideScroll = motion(hideScrollbars(Box));
const MotionSubRegionsTabs = motion(SubRegionsTabs);

type MotionBoxProps = MotionPropsFor<BoxProps>;

interface ArrowButtonProps
  extends MotionPropsFor<IntrinsicSystemProps<'button'>> {
  direction: 'left' | 'right';
}

const ArrowButton = forwardRef<ArrowButtonProps, 'button'>(
  ({ direction, sx, ...props }, ref) => {
    const exit = direction === 'left' ? 10 : -10;

    return (
      <motion.button
        alignItems="center"
        animate={{ x: 0, opacity: 1 }}
        display={{ base: 'none', tablet: 'flex' }}
        exit={{ x: exit, opacity: 0 }}
        height="300"
        initial={{ x: exit, opacity: 0 }}
        justifyContent="center"
        position="absolute"
        sx={{ ...buttonStyles, sx }}
        width="300"
        {...props}
        ref={ref}
      >
        <Icon
          icon={direction === 'left' ? iconChevronLeft : iconChevronRight}
          m={0}
          size="small"
          transform={`translateX(${direction === 'left' ? -1 : 1}px)`}
        />
      </motion.button>
    );
  },
);

const footerGap = {
  base: 500,
  tablet: 600,
} as const satisfies Record<string, keyof Space>;

/**
 * The footer displayed below the history image in the `CustomizeHistory` view
 * has somewhat complex logic to determine whether the footer and its different
 * possible children should be visible, so this hook encapsulates that logic and
 * returns simnple booleans representing the visiblity.
 */
export const useCustomizeFooterVisibility = () => {
  const { category } = useCustomization();
  const { selectionMode } = useCustomizationSegmentation();

  const { subRegions } = useSubRegions();
  const isSubregionSelectionEnabled = useIsSubregionSelectionEnabled();

  const isMobile = useBreakpointValue(
    { base: true, tablet: false },
    { fallback: 'tablet' },
  );

  const isDisplayingSubregions =
    isMobile ||
    subRegions.length > 0 ||
    (isSubregionSelectionEnabled && selectionMode === 'instance');

  const isDisplayingMaterialCallout =
    !!useCustomizationMaterialCallout() && !isMobile;

  return {
    isVisible:
      category && (isDisplayingSubregions || isDisplayingMaterialCallout),
    isDisplayingSubregions,
    isDisplayingMaterialCallout,
  };
};

export interface CustomizeFooterDimensions {
  /**
   * Current height of the footer contents
   */
  height: string;
  /**
   * The height of the subregion tabs and/or select palette button
   */
  subregionsHeight: string;
  /**
   * The height of the material callout button
   */
  calloutHeight: string;
  /**
   * The offset needed to accomodate the footer beneath the history image (this
   * includes the gap between the footer itself and the image)
   */
  offset: string;
  /**
   * The gap used to separate the footer from the history image and to separate the elements within the footer
   */
  gap: string;
}

/**
 * Because we need to absolutely position the footer relative to the bottom of
 * the image displayed in `CustomizeHistory`, we need to explicitly calculate
 * the height of the footer and provide an offset that accounts for the space
 * the footer takes up below the history image. This hook calculates the overall
 * height of the footer and the offset needed to accomodate it depending on
 * what's being displayed in the footer.
 */
export const useCustomizeFooterDimensions = (): CustomizeFooterDimensions => {
  const { sizes, space } = useTheme();
  const { isVisible, isDisplayingMaterialCallout, isDisplayingSubregions } =
    useCustomizeFooterVisibility();

  const isMobile = useBreakpointValue(
    { base: true, tablet: false },
    { fallback: 'tablet' },
  );

  const gap = space[footerGap[isMobile ? 'base' : 'tablet']];

  const subregionsHeight = calc.add(sizes[400], space[200]);
  const calloutHeight = calc.add(sizes[500]);

  const gaps = [isDisplayingSubregions, isDisplayingMaterialCallout].filter(
    isTruthy,
  ).length;

  const height = calc.add(
    isDisplayingSubregions ? subregionsHeight : '0px',
    isDisplayingMaterialCallout ? calloutHeight : '0px',
    calc.multiply(gap, gaps - 1),
  );

  return {
    height,
    subregionsHeight,
    calloutHeight,
    gap,
    offset: isVisible ? calc.add(height, gap) : '0px',
  };
};

export interface CustomizeHistoryFooterProps
  extends Omit<MotionBoxProps, 'children'> {
  children?: never;
  scrollPadding?: MotionBoxProps['padding'];
}

const $footerGap = cssVar('customize-footer-gap');

export const CustomizeHistoryFooter = forwardRef<
  CustomizeHistoryFooterProps,
  'div'
>(({ scrollPadding, style, sx, ...props }, ref) => {
  const t = useHomeownerTranslate();

  const belowTablet = useMaxWidthQuery('tablet');
  const scrollRef = useRef<HTMLDivElement>(null);

  const [scrollTo] = useScrollTo({ ref: scrollRef });
  const {
    style: scrollStyle,
    isFadingEnd,
    isFadingStart,
  } = useScrollFade({
    ref: scrollRef,
    direction: 'horizontal',
    distance: '100px',
    threshold: '20px',
    padding: '0px',
  });

  const { colors, space } = useTheme();

  const { isCustomizing } = useCustomization();
  const materialCallout = useCustomizationMaterialCallout();

  const { calloutHeight, height, subregionsHeight } =
    useCustomizeFooterDimensions();
  const { isDisplayingSubregions, isDisplayingMaterialCallout } =
    useCustomizeFooterVisibility();

  return (
    <MotionFlex
      alignItems="center"
      bottom={calc.multiply($footerGap, -1)}
      direction="column"
      height={height}
      justifyContent="space-between"
      minHeight={subregionsHeight}
      position="relative"
      ref={ref}
      style={style}
      sx={{
        ...sx,
        ...transition.easing500('height'),
        ...belowTablet({ transition: 'none' }),
        [$footerGap.variable]: mapValues(footerGap, gap => space[gap]),
      }}
      {...props}
    >
      <Center
        height={subregionsHeight}
        opacity={isDisplayingSubregions ? 1 : 0}
        width="100%"
        {...transition.easing250('opacity')}
      >
        <MotionBoxHideScroll
          alignItems="center"
          bottom={0}
          boxSizing="border-box"
          flexDirection="row"
          gap="200"
          height="100%"
          margin="auto"
          overflowX="scroll"
          position={{ base: 'absolute', tablet: 'relative' }}
          px={scrollPadding}
          ref={scrollRef}
          style={scrollStyle}
          sx={{
            ...belowTablet({
              WebkitMaskImage: 'none !important',
              maskImage: 'none !important',
            }),
          }}
          top={0}
          width={{ base: '100vw', tablet: '100%' }}
        >
          <Flex
            alignItems="inherit"
            flexDirection="inherit"
            gap="inherit"
            height="100%"
            marginX="auto"
          >
            <CustomizeSelectPaletteButton
              display={{
                base: 'flex',
                tablet: 'none',
              }}
            />
            {isDisplayingSubregions && (
              <MotionSubRegionsTabs
                transition={{ duration: 0.25 }}
                {...fadePresence}
              />
            )}
          </Flex>
        </MotionBoxHideScroll>
        <Motion.Firm>
          <AnimatePresence initial={false}>
            {isFadingStart && (
              <ArrowButton
                aria-label={t('selector.scrollLeft')}
                direction="left"
                key="scroll-left"
                left={scrollPadding}
                onClick={() => scrollTo('left')}
              />
            )}
            {isFadingEnd && (
              <ArrowButton
                aria-label={t('selector.scrollRight')}
                direction="right"
                key="scroll-right"
                onClick={() => scrollTo('right')}
                right={scrollPadding}
              />
            )}
          </AnimatePresence>
        </Motion.Firm>
      </Center>
      <Motion.Soft>
        <AnimatePresence mode="popLayout">
          {isDisplayingMaterialCallout && materialCallout && (
            <MotionMaterialCalloutButton
              animate={{ opacity: 1, transition: { delay: 0.2 } }}
              bg={colors.alpha(0.1, 'neutral.900')}
              bottom={0}
              calloutUrl={materialCallout}
              colorMode="dark"
              exit={{ opacity: 0 }}
              height={calloutHeight}
              initial={{ opacity: 0 }}
              isLoading={isCustomizing}
              key="callout"
              position="absolute"
              size="large"
              zIndex={1}
            />
          )}
        </AnimatePresence>
      </Motion.Soft>
    </MotionFlex>
  );
});
